import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Assets,
  Avatar,
  Button,
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  Link,
  MaterialIcon,
  PluginCard,
  Select,
  styledComponent,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';
import { getWarningIcon } from '../SeverityIcon';
import { getCircleIcon } from '../StateIcon';
import { Link as RouterLink } from 'react-router-dom';
import { severityDropDownOption } from '../config';
import {
  DateUtil,
  useUserEntity,
} from '@dewise-devex/backstage-plugin-utilities-common-client';
import { useApi } from '@backstage/core-plugin-api';
import { productApiRef } from '../../plugin';
import useAsync from 'react-use/esm/useAsync';
import { unassignedMember } from '../unassignedMember';
import { useUserProfile } from '@backstage/plugin-user-settings';

const IconContainer = styledComponent('span')(({ theme }) => ({
  display: 'flex',
  '& svg': {
    fill: theme.palette.primary.onColor,
  },
}));

const TableContainer = styledComponent('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
}));

const TableTitle = styledComponent('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '10px',
}));

const allMem = { name: 'All Team Members', id: 'all', icon: '' };

const columns: GridColDef[] = [
  {
    field: 'productName',
    headerName: 'Product',
    minWidth: 125,
    flex: 0.7,
    renderCell: params => {
      return <Typography variant="bodyMD">{params.value}</Typography>;
    },
  },
  {
    field: 'state',
    headerName: 'State',
    headerAlign: 'center',
    minWidth: 60,
    flex: 0.5,
    align: 'center',
    disableColumnMenu: true,
    sortable: false,
    renderCell: params => {
      return (
        <Tooltip title={<>{params.value}</>}>
          {getCircleIcon(params.value as WorkItemState)}
        </Tooltip>
      );
    },
  },
  {
    field: 'severity',
    headerName: 'Severity',
    headerAlign: 'center',
    minWidth: 80,
    flex: 0.5,

    align: 'center',
    disableColumnMenu: true,
    sortable: false,
    renderCell: params => {
      return (
        <Tooltip title={<>{`Severity ${params.value as string}`}</>}>
          <span>{getWarningIcon(params.value as WorkItemSeverity)}</span>
        </Tooltip>
      );
    },
  },
  {
    field: 'title',
    headerName: 'Title',
    minWidth: 100,
    flex: 3,
    renderCell: params => {
      return (
        <Link
          component={RouterLink}
          to={`/work-item/${params.row.id}`}
          state={params.row}
        >
          {params.value}
        </Link>
      );
    },
  },
  {
    field: 'createdDateTime',
    headerName: 'Created on',
    minWidth: 145,
    flex: 1,
    renderCell: params => {
      return (
        <Typography variant="bodyMD">
          {DateUtil.toWeekDayMonthDayYear(String(params.value))}
        </Typography>
      );
    },
  },
  {
    field: 'assignedTo',
    headerName: 'Assigned to',
    minWidth: 147,
    flex: 1.5,
    valueGetter: (params: GridValueGetterParams) => {
      const assginedToMeta = params.value as AssignedTo[];
      const name = assginedToMeta[0].isUser
        ? 'Me'
        : assginedToMeta[0].displayName;
      return name;
    },
    renderCell: params => {
      return (
        <Typography variant="bodyMD" sx={{ fontSize: '14px' }}>
          {params.value}
        </Typography>
      );
    },
  },
];

export const OpenBugs: React.FC = () => {
  const navigate = useNavigate();
  const [productId, setProductId] = useState<string>('all');
  const [pageSize, setPageSize] = useState<number>(5);
  const [assignedToId, setAssignedToId] = useState<string>('');
  const [severity, setSeverity] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState(0);
  const { profile } = useUserProfile();
  const productService = useApi(productApiRef);
  const userEntityService = useUserEntity();

  const { value: entity } = useAsync(async () => {
    if (profile?.email) {
      setAssignedToId(profile.email);
      return await userEntityService.byEmail(profile.email);
    }

    return undefined;
  }, [profile?.email]);

  const {
    value: { productDropdown = [], assignedToDropdown = [allMem] } = {},
    loading: isFilterValLoading,
  } = useAsync(async () => {
    const { product, assignedTo } =
      await productService.getFilterValuesForWorkItem();
    let user;
    const teamMembers = assignedTo
      ?.reduce((teamMembersMeta: TeamMemberDropdown[], curr) => {
        const member = {
          id: curr.uniqueName,
          name: curr.isUser ? 'Me' : curr.displayName,
          img: () => {
            if (curr.uniqueName === 'Unassigned') {
              return unassignedMember.img();
            } else {
              return (
                <Avatar
                  sx={{ width: 24, height: 24 }}
                  src={curr.profileImg}
                  alt={curr.uniqueName}
                />
              );
            }
          },
        };
        if (curr.isUser) {
          setAssignedToId(curr.uniqueName);
          user = member;
        } else {
          teamMembersMeta.push(member);
        }

        return teamMembersMeta;
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name));

    if (product.length === 0) setAssignedToId('all');

    return {
      productDropdown: product,
      assignedToDropdown: user
        ? [allMem, user, ...teamMembers]
        : [allMem, ...teamMembers],
    };
  });

  const {
    value: workItemsMeta = { workItems: [], totalCount: 0 },
    loading: isWorkItemsLoading,
  } = useAsync(async () => {
    return await productService.getWorkItems({
      products:
        productId === 'all'
          ? undefined
          : `${entity?.metadata.namespace}/${productId}`,
      assignedTo: assignedToId === 'all' ? undefined : assignedToId,
      severity: severity === 'all' ? undefined : severity,
      limit: pageSize,
      page: Number(page + 1),
      search: searchQuery,
    });
  }, [productId, assignedToId, severity, pageSize, page, entity, searchQuery]);

  return (
    <PluginCard
      title="Open Bugs"
      subheader="These are items that needs urget attention"
      image={{
        light: Assets.bug,
        dark: Assets.bug,
        size: { width: '37px', height: '37px' },
      }}
      isContentMuiDataGridOnly
      action={
        <Button
          variant="contained"
          onClick={() => {
            navigate('/work-item');
          }}
          startIcon={
            <IconContainer>
              <SvgIcon.add />
            </IconContainer>
          }
        >
          Report Bug
        </Button>
      }
    >
      <TableContainer>
        <TableTitle>
          <Select
            formSx={{ width: '200px' }}
            label="Select Product"
            value={productId}
            onChange={value => {
              setProductId(value);
              setPage(0);
            }}
            displayMapper={{
              id: 'productId',
              name: 'productName',
            }}
            options={[
              { productId: 'all', productName: 'All Products' },
              ...productDropdown,
            ]}
          />
          <Select
            formSx={{ width: '200px' }}
            label="Select Assigned To"
            value={assignedToId}
            isLoading={isFilterValLoading}
            loadingOption={{
              title: 'Me',
              icon: () => (
                <Avatar
                  sx={{ width: 24, height: 24 }}
                  src={profile.picture}
                  alt={profile.displayName}
                />
              ),
            }}
            onChange={value => {
              setAssignedToId(value);
              setPage(0);
            }}
            displayMapper={{
              id: 'id',
              name: 'name',
              icon: 'img',
            }}
            options={assignedToDropdown}
          />
          <Select
            formSx={{ width: '300px' }}
            label="Severity"
            value={severity}
            onChange={value => {
              setSeverity(value);
              setPage(0);
            }}
            displayMapper={{
              id: 'id',
              name: 'name',
              icon: 'icon',
            }}
            options={[
              { id: 'all', name: 'All Severities' },
              ...severityDropDownOption,
            ]}
          />
        </TableTitle>
        <TextField
          formSx={{ width: '200px' }}
          placeholder="Search…"
          onChange={e => setSearchQuery(e.target.value)}
          value={searchQuery}
          startAdornment={
            <MaterialIcon.search
              fontSize="small"
              sx={{ marginRight: '10px' }}
            />
          }
        />
      </TableContainer>
      <DataGrid
        columns={columns}
        rows={workItemsMeta.workItems}
        loading={isWorkItemsLoading}
        emptyMessageTitle="No Data Available"
        emptyMessageDescription="It looks like there’s currently no data to display. Please check back later or adjust your search criteria."
        pagination
        rowCount={workItemsMeta.totalCount}
        pageSize={pageSize}
        onPageSizeChange={newPageSize => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        rowsPerPageOptions={[5, 15, 25, 100]}
        onPageChange={pageNum => setPage(pageNum)}
        page={page}
        paginationMode="server"
      />
    </PluginCard>
  );
};
